import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { LOGO_LIGHT } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const links = [
    { to: "/", label: "Home" },
    { to: "/platform", label: "How It Works" },
    { to: "/pricing", label: "Pricing" },
    { to: "/premium-support", label: "Premium Support" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
    { to: "/contact", label: "Contact" },
];

export default function MarketingNav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="marketing-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? "glass-header border-b border-stone-200/60" : "bg-transparent"}`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
                    <img src={LOGO_LIGHT} alt="Energize.fit" className="h-11 w-11 object-contain" />
                    <span className="font-heading text-xl tracking-tight text-stone-900">Energize<span className="text-teal-500">.fit</span></span>
                </Link>
                <nav className="hidden lg:flex items-center gap-8">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm transition-colors ${isActive ? "text-stone-900 font-semibold" : "text-stone-600 hover:text-stone-900"}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="hidden lg:flex items-center gap-3">
                    {user ? (
                        <button
                            onClick={() => nav("/app")}
                            data-testid="nav-dashboard-btn"
                            className="inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform"
                        >
                            Dashboard <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <>
                            <Link to="/login" data-testid="nav-login-link" className="text-sm text-stone-700 hover:text-stone-900">Log in</Link>
                            <Link to="/signup" data-testid="nav-signup-btn" className="inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">
                                Get Started <ArrowRight className="w-4 h-4" />
                            </Link>
                        </>
                    )}
                </div>
                <button
                    data-testid="nav-mobile-toggle"
                    className="lg:hidden p-2 rounded-lg hover:bg-stone-100"
                    onClick={() => setOpen((v) => !v)}
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>
            {open && (
                <div className="lg:hidden border-t border-stone-200 bg-white">
                    <div className="px-6 py-4 flex flex-col gap-3">
                        {links.map((l) => (
                            <Link
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                onClick={() => setOpen(false)}
                                className="text-stone-800 py-2 font-medium"
                            >
                                {l.label}
                            </Link>
                        ))}
                        {user ? (
                            <Link to="/app" onClick={() => setOpen(false)} className="bg-stone-900 text-white rounded-full px-5 py-3 text-sm font-medium text-center">Dashboard</Link>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setOpen(false)} className="text-stone-800 py-2 font-medium">Log in</Link>
                                <Link to="/signup" onClick={() => setOpen(false)} className="bg-stone-900 text-white rounded-full px-5 py-3 text-sm font-medium text-center">Get Started</Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
